import { baseApi } from '@/store/baseApi';
import { CacheDurations } from '@web/utils';

import type {
  CreateTaskAssigneePayload,
  CreateTaskAssigneeResponse,
  CreateTaskPayload,
  CreateTaskResponse,
  DeleteTaskAssigneeResponse,
  DeleteTaskResponse,
  GetTasksParams,
  GetTasksResponse,
  UpdateTaskAssigneePayload,
  UpdateTaskAssigneeResponse,
  UpdateTaskPayload,
  UpdateTaskResponse,
} from '@/features/tasks/types';
import type { APIResponse } from '@web/types';

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<
      APIResponse<GetTasksResponse>,
      GetTasksParams | void
    >({
      query: (params) => ({
        url: 'tasks',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: [{ type: 'Tasks', id: 'LIST' }],
    }),
    createTask: builder.mutation<
      APIResponse<CreateTaskResponse>,
      CreateTaskPayload
    >({
      query: (body) => ({
        url: 'tasks',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Tasks', id: 'LIST' }],
    }),
    updateTask: builder.mutation<
      APIResponse<UpdateTaskResponse>,
      { taskId: string; payload: UpdateTaskPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: `tasks/${taskId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Tasks', id: 'LIST' }],
    }),
    createTaskAssignee: builder.mutation<
      APIResponse<CreateTaskAssigneeResponse>,
      { taskId: string; payload: CreateTaskAssigneePayload }
    >({
      query: ({ taskId, payload }) => ({
        url: `tasks/${taskId}/assignees`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Tasks', id: 'LIST' }],
    }),
    updateTaskAssignee: builder.mutation<
      APIResponse<UpdateTaskAssigneeResponse>,
      { taskAssigneeId: string; payload: UpdateTaskAssigneePayload }
    >({
      query: ({ taskAssigneeId, payload }) => ({
        url: `tasks/assignees/${taskAssigneeId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Tasks', id: 'LIST' }],
    }),
    deleteTaskAssignee: builder.mutation<
      APIResponse<DeleteTaskAssigneeResponse>,
      { taskAssigneeId: string }
    >({
      query: ({ taskAssigneeId }) => ({
        url: `tasks/assignees/${taskAssigneeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Tasks', id: 'LIST' }],
    }),
    deleteTask: builder.mutation<
      APIResponse<DeleteTaskResponse>,
      { taskId: string }
    >({
      query: ({ taskId }) => ({
        url: `tasks/${taskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Tasks', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useLazyGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useCreateTaskAssigneeMutation,
  useUpdateTaskAssigneeMutation,
  useDeleteTaskAssigneeMutation,
  useDeleteTaskMutation,
} = tasksApi;
